import React from 'react';
import { ChevronDown } from 'lucide-react';
import GlassCard from './ui/GlassCard';
import ScrollReveal from './ui/ScrollReveal';

type FAQItem = {
  question: string;
  answer: string;
};

export const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = React.useState<number | null>(0);

  const faqs: FAQItem[] = [
    {
      question: 'Who is Sudipta Dhara?',
      answer: 'Sudipta Dhara is a Software Developer and Generative AI Engineer with over 14 years of experience in building robust applications, scalable backend systems, and cutting-edge AI solutions. Based in Kolkata, West Bengal, India, he specializes in backend development, generative AI, and creating innovative technology products.'
    },
    {
      question: 'What does Sudipta Dhara do?',
      answer: 'Sudipta Dhara is a Lead Generative AI Developer at DECA Games, where he builds production-ready RAG pipelines, agentic AI systems, and LLM applications using LangChain, LangGraph, vector databases, and modern observability tooling. He is also the creator of 7 innovative tech products including Talk2Term, MegaSearch, ProDev\'s News, MeetEcho, RitualRhythms, MySmartNest, and ProDevs.'
    },
    {
      question: 'Where is Sudipta Dhara located?',
      answer: 'Sudipta Dhara is based in Kolkata, West Bengal, India. He works as a Lead Generative AI Developer and Game Backend Developer at DECA Games.'
    },
    {
      question: 'What products has Sudipta Dhara developed?',
      answer: 'Sudipta Dhara has developed 7 active products: Talk2Term (AI-powered terminal automation), MegaSearch (intelligent metasearch engine), ProDev\'s News (AI news aggregator), MeetEcho (AI transcription platform), RitualRhythms (society management platform), MySmartNest (smart home automation), and ProDevs (technology platform and blog).'
    },
    {
      question: 'What is Talk2Term?',
      answer: 'Talk2Term is a ZSH plugin that converts plain English into perfect shell commands using AI. It allows developers to stop googling CLI flags and instead type in English to get executable commands. The tool features GPT-powered command generation, free daily credits, and native ZSH integration.'
    },
    {
      question: 'How does Talk2Term work?',
      answer: 'Talk2Term works as a ZSH plugin that uses AI (GPT-powered) to convert natural language descriptions into executable shell commands. Users simply type in English what they want to do, and Talk2Term generates the appropriate command. It offers free daily credits and integrates seamlessly with ZSH terminals.'
    },
    {
      question: 'What is MegaSearch?',
      answer: 'MegaSearch is an intelligent metasearch engine that queries multiple sources simultaneously, analyzes results with AI, and synthesizes comprehensive answers with citations. It features parallel execution across 10+ search sources, AI-powered analysis and refinement, and native MCP server support for integration with AI agents.'
    },
    {
      question: 'How does MegaSearch work?',
      answer: 'MegaSearch works by querying multiple search sources in parallel, then using AI to analyze and refine the results. It performs up to 5 refinement loops to ensure comprehensive answers, synthesizes information from all sources, and provides citations. The service offers REST API, MCP server integration, and n8n node support for various use cases.'
    },
    {
      question: 'What is ProDev\'s News?',
      answer: 'ProDev\'s News is a personalized AI/ML news digest that automatically aggregates and deduplicates news from 50+ trusted sources. It features smart deduplication, personalized news feeds, audio podcast summaries, and real-time updates to help developers stay ahead in AI without the noise.'
    },
    {
      question: 'What is RitualRhythms?',
      answer: 'RitualRhythms is a comprehensive society management platform that streamlines operations, enhances resident experience, and builds stronger communities. It features event management, smart QR-based food coupons, donation management, member directory, analytics & reports, and is used by 500+ active societies across India.'
    },
    {
      question: 'What technologies does Sudipta Dhara use?',
      answer: 'Sudipta Dhara uses a wide range of technologies including Python, PHP, Django, Node.js for backend development; LangChain, LangGraph, OpenAI API for generative AI; Vector databases (Pinecone, FAISS) for RAG pipelines; FastAPI for API development; PostgreSQL, MySQL for databases; Docker, AWS for DevOps; and React, Next.js for frontend development.'
    },
    {
      question: 'How can I contact Sudipta Dhara?',
      answer: 'You can contact Sudipta Dhara through the contact form on this website, via WhatsApp, or by email. Visit the Contact section for more details and contact information.'
    },
    {
      question: 'What is Sudipta Dhara\'s experience?',
      answer: 'Sudipta Dhara has over 14 years of experience in software development. He currently serves as Lead Generative AI Developer and Game Backend Developer at DECA Games (since December 2019). Previously, he worked at HIGHBREED DEVELOPMENT, Digital Aptech, served as CTO at HobbyNetworking, and co-founded Packr and Paaltao. He has extensive experience in backend development, AI applications, and leading technical teams.'
    },
    {
      question: 'What companies has Sudipta Dhara worked for?',
      answer: 'Sudipta Dhara has worked for DECA Games (current - Lead Generative AI Developer and Game Backend Developer), HIGHBREED DEVELOPMENT INDIA PRIVATE LIMITED, Digital Aptech, HobbyNetworking (CTO), Packr (CTO and Co-founder), Paaltao (CTO and Co-founder), BlueHorse Software, and Soft Amass India.'
    }
  ];

  // Generate FAQPage schema
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema, null, 2) }}
      />
      <section id="faq" className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-ink">Frequently Asked Questions</h2>
            <div className="w-24 h-1 bg-brand mx-auto"></div>
            <p className="text-ink-dim mt-4 max-w-2xl mx-auto">
              Common questions about Sudipta Dhara, his work, and the products he has developed
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <ScrollReveal key={index}>
                <GlassCard className="overflow-hidden">
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-white/5 transition-colors"
                    aria-expanded={openIndex === index}
                  >
                    <h3 className="text-lg md:text-xl font-semibold text-ink pr-8">
                      {faq.question}
                    </h3>
                    <ChevronDown
                      size={24}
                      className={`text-brand flex-shrink-0 transition-transform ${
                        openIndex === index ? 'transform rotate-180' : ''
                      }`}
                    />
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="px-6 pb-6 text-ink-dim leading-relaxed">
                      {faq.answer}
                    </div>
                  </div>
                </GlassCard>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

